import {
  InstagramAuditInput,
  InstagramAuditResult,
  AuditScoreCategory,
  InstagramPublicData,
  InstagramInsightsData,
  ScoreBreakdownItem,
  PriorityActionItem,
  CompetitorMetricComparison,
} from '../../../types/socialMediaAudit';

const CTA_VERBS = [
  'shop', 'discover', 'explore', 'book', 'join', 'claim', 'download', 'tap',
  'click', 'visit', 'order', 'try', 'get', 'start', 'subscribe', 'dm', 'apply', 'learn'
];

const AUTHORITY_SIGNALS = [
  'official', 'est', 'founded', 'certified', 'award', 'patented', 'clinical',
  'dermatologist', 'artisan', 'handcrafted', 'sustainable', 'vegan', 'cruelty-free',
  'global', 'studio', 'lab', 'labs', 'atelier', 'maison', 'roasters', 'collective'
];

export class InstagramScoringEngine {
  public generateAudit(input: InstagramAuditInput): InstagramAuditResult {
    const cleanUsername = this.normalizeHandle(input.profileData.username);
    const normalizedProfile: InstagramPublicData = {
      ...input.profileData,
      username: cleanUsername,
      brandName: input.profileData.brandName?.trim() || input.profileData.fullName?.trim() || cleanUsername,
      industry: input.profileData.industry?.trim() || 'General Brand',
    };

    const brandIdentity = this.evaluateBrandIdentityAndHandle(normalizedProfile);
    const bioArchitecture = this.evaluateBioAndConversionArchitecture(normalizedProfile);
    const contentStrategy = this.evaluateContentAndVisualStrategy(normalizedProfile);
    const copyAndHashtags = this.evaluateCopywritingAndDiscoverability(normalizedProfile);
    const engagementTrust = this.evaluateEngagementAndAudienceTrust(normalizedProfile, input.insightsData);
    const growthFunnel = this.evaluateGrowthAndFunnelReadiness(normalizedProfile, input.insightsData);

    const categories: AuditScoreCategory[] = [
      brandIdentity,
      bioArchitecture,
      contentStrategy,
      copyAndHashtags,
      engagementTrust,
      growthFunnel,
    ];

    const totalWeight = categories.reduce((sum, cat) => sum + cat.weight, 0);
    const overallScore = Math.round(
      categories.reduce((sum, cat) => sum + cat.score * cat.weight, 0) / totalWeight
    );

    const grade = this.computeGrade(overallScore);
    const riskLevel = this.computeRiskLevel(overallScore);
    const brandArchetype = this.determineBrandArchetype(overallScore, categories);
    const dataCompleteness = this.evaluateDataCompleteness(normalizedProfile, input.insightsData, input.competitorData);
    const priorityActions = this.buildPriorityActionMatrix(categories);

    const rawEr = this.calculateEngagementRatePct(normalizedProfile);
    const ffRatio = normalizedProfile.followingCount > 0
      ? Number((normalizedProfile.followersCount / normalizedProfile.followingCount).toFixed(2))
      : normalizedProfile.followersCount;

    const competitorComparison = input.competitorData && input.competitorData.username.trim()
      ? this.evaluateCompetitor(normalizedProfile, input.competitorData)
      : undefined;

    return {
      overallScore,
      grade,
      riskLevel,
      brandArchetype,
      platform: 'instagram',
      auditedAt: new Date().toISOString(),
      handle: cleanUsername,
      brandName: normalizedProfile.brandName || cleanUsername,
      industry: normalizedProfile.industry || 'General Brand',
      targetAudience: normalizedProfile.targetAudience,
      primaryBrandGoal: normalizedProfile.primaryBrandGoal,
      dataCompleteness,
      keyMetricsSnapshot: {
        engagementRatePct: Number(rawEr.toFixed(2)),
        engagementTierLabel: this.getEngagementTierLabel(rawEr, normalizedProfile.followersCount),
        followerToFollowingRatio: ffRatio,
        postingCadencePerWeek: normalizedProfile.postsPerWeek ?? Math.min(7, Math.max(1, normalizedProfile.recentPosts.length)),
        handleHygieneScore: brandIdentity.score,
        bioConversionScore: bioArchitecture.score,
      },
      categories,
      priorityActions,
      competitorComparison,
      executiveSummary: this.generateExecutiveSummary(normalizedProfile, overallScore, grade, categories),
      consultantMethodologyNote:
        'Generated via the BrandGenius Deterministic Social Audit Engine v2.4. All scores are mathematically derived from reproducible linguistic, structural, cadence, and engagement heuristics—with zero generative AI or opaque black-box estimations.',
    };
  }

  public normalizeHandle(rawInput: string): string {
    let cleaned = rawInput.trim();
    cleaned = cleaned.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '');
    cleaned = cleaned.split('/')[0].split('?')[0];
    cleaned = cleaned.replace(/^@/, '');
    return cleaned.toLowerCase() || 'brand';
  }

  private computeGrade(score: number): 'A+' | 'A' | 'B' | 'C' | 'D' | 'F' {
    if (score >= 92) return 'A+';
    if (score >= 82) return 'A';
    if (score >= 70) return 'B';
    if (score >= 58) return 'C';
    if (score >= 45) return 'D';
    return 'F';
  }

  private computeRiskLevel(score: number): 'Low' | 'Medium' | 'High' {
    if (score >= 75) return 'Low';
    if (score >= 55) return 'Medium';
    return 'High';
  }

  // ---------------------------------------------------------------------------
  // PILLAR 1: Brand Identity, Handle Hygiene & Searchable Name SEO
  // ---------------------------------------------------------------------------
  private evaluateBrandIdentityAndHandle(data: InstagramPublicData): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    const handle = data.username;
    const hasUnderscoreOrDot = /[._]/.test(handle);
    const hasNumbers = /\d/.test(handle);
    const consecutiveSymbols = /[._]{2,}|\d{3,}/.test(handle);

    // 1. Handle Orthographic Cleanliness (25 pts)
    let handlePts = 25;
    if (consecutiveSymbols) {
      handlePts = 8;
      findings.push(`Handle (@${handle}) contains consecutive punctuation or numeric strings that reduce recall and signal unofficial status.`);
      recommendations.push('Eliminate trailing numbers or multiple underscores in @handle. Use a clean prefix/suffix like @wear[brand], @get[brand], or @[brand]official.');
    } else if (hasUnderscoreOrDot || hasNumbers) {
      handlePts = 16;
      findings.push(`Handle (@${handle}) contains punctuation or digits, adding friction to word-of-mouth search and podcast/video mentions.`);
      recommendations.push('Where possible, consolidate to a single uninterrupted alphanumeric handle so users do not mistype periods or underscores.');
    } else {
      findings.push(`Clean, uninterrupted handle (@${handle}) with zero orthographic friction.`);
    }

    breakdown.push({
      criterion: 'Handle Orthographic Hygiene (Punctuation & Numeric Friction)',
      pointsEarned: handlePts,
      maxPoints: 25,
      status: handlePts >= 22 ? 'optimal' : handlePts >= 15 ? 'moderate' : 'deficit',
      rationale: handlePts === 25 ? 'Zero underscores, periods, or digits detected.' : 'Special characters or digits increase recall friction.',
    });

    // 2. Handle Length & Phonetic Brevity (20 pts)
    let lengthPts = 20;
    if (handle.length <= 14 && handle.length >= 4) {
      lengthPts = 20;
      findings.push(`Handle length (${handle.length} characters) sits within the optimal 4–14 character memorability window.`);
    } else if (handle.length <= 18) {
      lengthPts = 14;
      findings.push(`Handle length (${handle.length} characters) is slightly long for mobile header truncation.`);
      recommendations.push('Keep Instagram handles under 15 characters to prevent truncation in Reels overlays and Story tags.');
    } else {
      lengthPts = 7;
      findings.push(`Handle length (${handle.length} characters) exceeds 18 characters, creating high typing error rates.`);
      recommendations.push('Shorten the handle to a concise brand root (<15 characters) to improve mobile readability.');
    }

    breakdown.push({
      criterion: 'Handle Length & Mobile Truncation Resistance',
      pointsEarned: lengthPts,
      maxPoints: 20,
      status: lengthPts >= 18 ? 'optimal' : lengthPts >= 12 ? 'moderate' : 'deficit',
      rationale: `${handle.length} characters evaluated against optimal 4–14 char benchmark.`,
    });

    // 3. Display Name Instagram SEO Alignment (25 pts)
    const fullName = (data.fullName || '').trim();
    const hasIndustryKeywordInName =
      fullName.length > handle.length + 3 &&
      (fullName.includes('|') || fullName.includes('•') || fullName.includes('-') || fullName.split(/\s+/).length >= 2);

    let nameSeoPts = 10;
    if (hasIndustryKeywordInName && fullName.length <= 45) {
      nameSeoPts = 25;
      findings.push(`Display Name ("${fullName}") pairs the brand name with searchable category keywords for Instagram Graph Search.`);
    } else if (fullName.length > 0 && fullName.toLowerCase() !== handle.toLowerCase()) {
      nameSeoPts = 16;
      findings.push(`Display Name ("${fullName}") is distinct from the handle but underutilizes high-intent category keywords.`);
      recommendations.push(`Format your Display Name as "${data.brandName || fullName} | ${data.industry || 'Category Keyword'}" to rank in Instagram keyword search.`);
    } else {
      nameSeoPts = 8;
      findings.push('Display Name merely repeats the username without any searchable category descriptor.');
      recommendations.push(`Add your primary category keyword to the Name field (e.g., "${data.brandName} • ${data.industry || 'Official'}")—this field is indexed by Instagram Search.`);
    }

    breakdown.push({
      criterion: 'Display Name Search Engine Optimization (Name Field Keywords)',
      pointsEarned: nameSeoPts,
      maxPoints: 25,
      status: nameSeoPts >= 22 ? 'optimal' : nameSeoPts >= 15 ? 'moderate' : 'deficit',
      rationale: hasIndustryKeywordInName ? 'Includes brand name + searchable descriptor.' : 'Missing category keyword in indexed Name field.',
    });

    // 4. Visual Avatar & Account Configuration (30 pts)
    let configPts = 0;
    if (data.isBusinessAccount) {
      configPts += 12;
      findings.push('Configured as a Professional/Business profile with category label and analytics capability.');
    } else {
      recommendations.push('Convert to an Instagram Professional/Business account to unlock action buttons, category badges, and Insights.');
    }

    if (data.profilePicType === 'brand_logo' || data.profilePicType === 'founder_portrait' || !data.profilePicType) {
      configPts += 12;
      findings.push('High-contrast brand mark / avatar configured for 110×110px circular crop legibility.');
    } else {
      configPts += 4;
      recommendations.push('Replace low-contrast or text-heavy profile image with a bold icon mark centered with 20% safe-zone padding.');
    }

    if (data.isVerified || data.hasContactButtons !== false) {
      configPts += 6;
    }

    breakdown.push({
      criterion: 'Professional Account Configuration & Avatar Legibility',
      pointsEarned: configPts,
      maxPoints: 30,
      status: configPts >= 24 ? 'optimal' : configPts >= 16 ? 'moderate' : 'deficit',
      rationale: 'Evaluates professional account tier, contact triggers, and circular avatar legibility.',
    });

    const totalScore = Math.min(100, handlePts + lengthPts + nameSeoPts + configPts);

    return {
      categoryId: 'brand_identity_profile',
      categoryName: 'Brand Identity & Handle SEO',
      shortLabel: 'Identity & SEO',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'Orthographic cleanliness of @handle, character length, Display Name keyword indexing, professional account status, and avatar recognition.',
      whyItMatters: 'Your @handle and Display Name are the only two fields heavily weighted by Instagram Search. Any punctuation friction or missing category keywords directly reduces organic discovery.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: 'Top-quartile consumer brands maintain handles ≤14 characters with zero underscores and a "Brand | Category" Display Name structure.',
      weight: 1.2,
    };
  }

  // ---------------------------------------------------------------------------
  // PILLAR 2: Bio Quality, Value Proposition & Conversion Architecture
  // ---------------------------------------------------------------------------
  private evaluateBioAndConversionArchitecture(data: InstagramPublicData): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    const bio = (data.bio || '').trim();
    const bioLength = bio.length;
    const lowerBio = bio.toLowerCase();

    // 1. Bio Length & Information Density (25 pts)
    let lengthPts = 0;
    if (bioLength >= 80 && bioLength <= 150) {
      lengthPts = 25;
      findings.push(`Bio utilizes ${bioLength}/150 characters—maximizing value proposition real estate without clutter.`);
    } else if (bioLength >= 45 && bioLength < 80) {
      lengthPts = 16;
      findings.push(`Bio length (${bioLength}/150 chars) communicates basics but leaves unused conversion space.`);
      recommendations.push('Expand bio to 90–145 characters to include a specific proof point, audience callout, and directional CTA.');
    } else if (bioLength > 150) {
      lengthPts = 18;
      findings.push(`Bio length (${bioLength} chars) exceeds standard 150-character viewport and may trigger a "more..." fold.`);
      recommendations.push('Tighten bio copy to ≤150 characters so your Call-to-Action remains visible without tapping "more".');
    } else {
      lengthPts = 6;
      findings.push(`Bio is critically thin (${bioLength} characters), failing to communicate positioning to first-time visitors.`);
      recommendations.push('Write a structured 3-line bio: (1) What you do & for whom, (2) Social proof or authority signal, (3) Actionable CTA pointing to your link.');
    }

    breakdown.push({
      criterion: 'Bio Character Utilization & Viewport Fit (80–150 Chars)',
      pointsEarned: lengthPts,
      maxPoints: 25,
      status: lengthPts >= 20 ? 'optimal' : lengthPts >= 14 ? 'moderate' : 'deficit',
      rationale: `Measured ${bioLength} characters against the 80–150 character sweet spot.`,
    });

    // 2. Authority & Proof Signals (25 pts)
    const matchedAuthority = AUTHORITY_SIGNALS.filter(sig => lowerBio.includes(sig));
    const hasSocialProofNumbers = /\d+[kKmM+]|\b(since|over|rated|trusted|#1)\b/i.test(bio);
    let authorityPts = 8;

    if (matchedAuthority.length >= 1 && hasSocialProofNumbers) {
      authorityPts = 25;
      findings.push('Bio incorporates both domain authority positioning and quantifiable proof signals.');
    } else if (matchedAuthority.length >= 1 || hasSocialProofNumbers) {
      authorityPts = 18;
      findings.push('Bio includes positioning terminology but could strengthen credibility with a concrete metric or credential.');
      recommendations.push('Add one concrete trust anchor to the bio (e.g., "Trusted by 25,000+ customers", "Dermatologist Formulated", or "Est. 2021").');
    } else {
      findings.push('No explicit authority markers, credentials, or quantitative social proof detected in bio copy.');
      recommendations.push('Insert an unmistakable credibility signal (certifications, customer count, or category specialty) on Line 2 of your bio.');
    }

    breakdown.push({
      criterion: 'Value Proposition & Credibility Proof Signals',
      pointsEarned: authorityPts,
      maxPoints: 25,
      status: authorityPts >= 20 ? 'optimal' : authorityPts >= 15 ? 'moderate' : 'deficit',
      rationale: matchedAuthority.length > 0 ? `Detected signals: ${matchedAuthority.slice(0, 3).join(', ')}` : 'Lacks quantitative or credential proof.',
    });

    // 3. Call-To-Action (CTA) Directive (25 pts)
    const matchedCta = CTA_VERBS.filter(verb => new RegExp(`\\b${verb}\\b`, 'i').test(bio));
    const hasDirectionalPointer = /[↓⬇👇→🔗]/.test(bio);
    let ctaPts = 5;

    if (matchedCta.length > 0 && hasDirectionalPointer) {
      ctaPts = 25;
      findings.push(`Strong action-oriented CTA ("${matchedCta[0].toUpperCase()}") paired with visual directional cue toward the bio link.`);
    } else if (matchedCta.length > 0) {
      ctaPts = 19;
      findings.push(`Action verb ("${matchedCta[0]}") present in bio copy.`);
      recommendations.push('Add a clear benefit-driven hook to the final line of your bio to increase tap-through rate on your external URL.');
    } else {
      findings.push('Bio lacks a direct imperative Call-to-Action verb telling profile visitors what to do next.');
      recommendations.push('End your bio with an explicit action line (e.g., "Shop the New Collection ↓" or "Book Your Free Strategy Audit ↓").');
    }

    breakdown.push({
      criterion: 'Imperative Call-To-Action (CTA) & Directional Cue',
      pointsEarned: ctaPts,
      maxPoints: 25,
      status: ctaPts >= 20 ? 'optimal' : ctaPts >= 15 ? 'moderate' : 'deficit',
      rationale: matchedCta.length > 0 ? `Detected CTA verb: "${matchedCta[0]}"` : 'No imperative CTA verb detected.',
    });

    // 4. External URL & Story Highlights Architecture (25 pts)
    let linkPts = 0;
    const extUrl = (data.externalUrl || '').trim();
    const highlights = data.highlightsCount ?? 4;

    if (extUrl) {
      const isDirectBrandDomain = !/(linktr\.ee|beacons\.ai|stan\.store|bit\.ly|taplink)/i.test(extUrl);
      if (isDirectBrandDomain && data.externalUrlType !== 'link_in_bio_tool') {
        linkPts += 17;
        findings.push(`Branded destination URL (${extUrl}) preserves domain authority and first-party pixel tracking.`);
      } else {
        linkPts += 12;
        findings.push(`Third-party link aggregator detected (${extUrl}).`);
        recommendations.push('Replace third-party link trees with a branded "/links" landing page on your own domain to retain brand equity and retargeting pixels.');
      }
    } else {
      findings.push('No external website URL configured in profile header—creating a dead-end funnel.');
      recommendations.push('Add a primary conversion URL in the Website field immediately to capture profile traffic.');
    }

    if (highlights >= 3 && highlights <= 7) {
      linkPts += 8;
      findings.push(`Story Highlights bar (${highlights} covers) acts as an evergreen secondary navigation menu.`);
    } else if (highlights > 0) {
      linkPts += 4;
      recommendations.push('Curate 4–6 core Story Highlights (About Us, Reviews, FAQs, Best Sellers, Press) to answer buyer objections on the profile.');
    } else {
      recommendations.push('Create at least 4 Story Highlights (Start Here, Product/Service, Client Proof, FAQ) beneath the bio.');
    }

    breakdown.push({
      criterion: 'External Link Equity & Story Highlights Navigation',
      pointsEarned: linkPts,
      maxPoints: 25,
      status: linkPts >= 20 ? 'optimal' : linkPts >= 12 ? 'moderate' : 'deficit',
      rationale: extUrl ? 'Active bio URL + Highlights evaluated.' : 'Missing primary conversion URL.',
    });

    const totalScore = Math.min(100, lengthPts + authorityPts + ctaPts + linkPts);

    return {
      categoryId: 'bio_conversion_architecture',
      categoryName: 'Bio & Conversion Architecture',
      shortLabel: 'Bio & Funnel',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'Bio character economy, value proposition clarity, authority proof markers, imperative CTA verbs, branded URL structure, and Story Highlights navigation.',
      whyItMatters: 'First-time visitors decide whether to follow or click your link within 3.2 seconds. A structured bio acts as a high-converting micro-landing page.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: 'High-converting brand profiles convert 8%–14% of profile visits into website clicks via a 3-line Bio + branded domain link.',
      weight: 1.3,
    };
  }

  // ---------------------------------------------------------------------------
  // PILLAR 3: Content Strategy, Cadence & Visual Grid Cohesion
  // ---------------------------------------------------------------------------
  private evaluateContentAndVisualStrategy(data: InstagramPublicData): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    const posts = data.recentPosts || [];
    const postsPerWeek = data.postsPerWeek ?? (posts.length >= 4 ? 4 : Math.max(1, posts.length));

    // 1. Publishing Cadence & Consistency (30 pts)
    let cadencePts = 0;
    if (postsPerWeek >= 4 && postsPerWeek <= 7) {
      cadencePts = 30;
      findings.push(`Publishing velocity (${postsPerWeek} posts/week) sustains algorithmic momentum without audience fatigue.`);
    } else if (postsPerWeek >= 2 && postsPerWeek < 4) {
      cadencePts = 21;
      findings.push(`Moderate publishing cadence (${postsPerWeek} posts/week) maintains baseline presence.`);
      recommendations.push('Increase publishing cadence to 4–5 feed posts/week (2 Reels, 2 Carousels, 1 Single Image) to accelerate non-follower reach.');
    } else if (postsPerWeek > 7) {
      cadencePts = 24;
      findings.push(`High-frequency publishing (${postsPerWeek} posts/week) detected; ensure quality per post does not cannibalize reach.`);
    } else {
      cadencePts = 9;
      findings.push(`Low publishing cadence (${postsPerWeek} post/week) limits algorithmic distribution in Explore and Reels tabs.`);
      recommendations.push('Establish a minimum consistent schedule of 3–4 feed posts per week paired with daily Story touchpoints.');
    }

    breakdown.push({
      criterion: 'Weekly Publishing Velocity & Algorithmic Consistency',
      pointsEarned: cadencePts,
      maxPoints: 30,
      status: cadencePts >= 25 ? 'optimal' : cadencePts >= 18 ? 'moderate' : 'deficit',
      rationale: `${postsPerWeek} posts/week evaluated against 4–7/week benchmark.`,
    });

    // 2. Multi-Format Distribution (Reels / Carousels / Static) (35 pts)
    const types = new Set(posts.map(p => p.type));
    const hasVideo = types.has('video');
    const hasCarousel = types.has('carousel');
    const hasImage = types.has('image');

    let formatPts = 12;
    if (hasVideo && hasCarousel && hasImage) {
      formatPts = 35;
      findings.push('Full-spectrum content mix: combines Reels (top-of-funnel discovery), Carousels (educational saves), and Static posts (brand aesthetics).');
    } else if ((hasVideo && hasCarousel) || types.size === 2) {
      formatPts = 26;
      findings.push(`Active dual-format strategy (${Array.from(types).join(' + ')}).`);
      if (!hasVideo) {
        recommendations.push('Integrate short-form Reels (7–15s and 30–45s) into the weekly mix to unlock non-follower discovery.');
      }
      if (!hasCarousel) {
        recommendations.push('Add multi-slide educational Carousels (6–10 slides) to drive Saves and dwell time.');
      }
    } else {
      formatPts = 12;
      findings.push('Single-format reliance restricts reach across Instagram’s separate feed, Reels, and Explore surfaces.');
      recommendations.push('Adopt the 40/40/20 format rule: 40% Reels for discovery, 40% Carousels for depth/saves, and 20% high-impact single images.');
    }

    breakdown.push({
      criterion: 'Format Diversity (Reels Discovery + Carousel Dwell Time)',
      pointsEarned: formatPts,
      maxPoints: 35,
      status: formatPts >= 30 ? 'optimal' : formatPts >= 22 ? 'moderate' : 'deficit',
      rationale: `Detected ${types.size} active content format(s).`,
    });

    // 3. Visual Grid Cohesion & Archive Depth (35 pts)
    let visualPts = 0;
    const gridConsistency = data.gridVisualConsistency || 'high';
    if (gridConsistency === 'high') {
      visualPts += 20;
      findings.push('High 9-grid visual cohesion with consistent typography, color temperature, and thumbnail hierarchy.');
    } else if (gridConsistency === 'moderate') {
      visualPts += 13;
      findings.push('Moderate visual consistency across recent grid rows.');
      recommendations.push('Standardize Reel cover thumbnails and brand typography across your top 9 grid posts to create an unmistakable visual system.');
    } else {
      visualPts += 6;
      findings.push('Inconsistent visual grid aesthetics dilute perceived brand equity.');
      recommendations.push('Define 2 brand fonts and a 3-color palette for all post covers so the top 9 tiles look like a cohesive editorial magazine.');
    }

    if (data.postsCount >= 45) {
      visualPts += 15;
      findings.push(`Established content archive (${data.postsCount} total posts) demonstrates brand longevity.`);
    } else if (data.postsCount >= 15) {
      visualPts += 10;
      findings.push(`Growing content library (${data.postsCount} total posts).`);
    } else {
      visualPts += 4;
      recommendations.push('Build your grid to at least 18–24 foundational posts so prospective buyers see an established brand archive.');
    }

    breakdown.push({
      criterion: '9-Grid Visual System & Content Archive Depth',
      pointsEarned: visualPts,
      maxPoints: 35,
      status: visualPts >= 28 ? 'optimal' : visualPts >= 18 ? 'moderate' : 'deficit',
      rationale: `${data.postsCount} total posts & ${gridConsistency} visual consistency.`,
    });

    const totalScore = Math.min(100, cadencePts + formatPts + visualPts);

    return {
      categoryId: 'content_visual_strategy',
      categoryName: 'Content & Visual Strategy',
      shortLabel: 'Content & Grid',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'Weekly publishing velocity, format balance across Reels/Carousels/Images, 9-grid visual cohesion, and archive depth.',
      whyItMatters: 'Instagram operates multiple distinct recommendation algorithms. Reels drive unconnected reach, while Carousels maximize dwell time and Saves.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: 'Leading brands publish 4–6 times/week with a 40% Reels / 40% Carousel / 20% Single Image distribution.',
      weight: 1.4,
    };
  }

  // ---------------------------------------------------------------------------
  // PILLAR 4: Copywriting, Hooks & Hashtag Discoverability
  // ---------------------------------------------------------------------------
  private evaluateCopywritingAndDiscoverability(data: InstagramPublicData): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    const posts = data.recentPosts || [];
    const avgCaptionLength = posts.length > 0
      ? Math.round(posts.reduce((sum, p) => sum + (p.caption || '').length, 0) / posts.length)
      : 0;
    const avgHashtags = posts.length > 0
      ? Number((posts.reduce((sum, p) => sum + (p.hashtags?.length || 0), 0) / posts.length).toFixed(1))
      : 0;

    // 1. Caption Storytelling & Hook Structure (40 pts)
    let captionPts = 15;
    if (avgCaptionLength >= 140 && avgCaptionLength <= 750) {
      captionPts = 40;
      findings.push(`Optimal caption depth (avg. ${avgCaptionLength} chars) supports both front-loaded hooks and substantive micro-storytelling.`);
    } else if (avgCaptionLength >= 60 && avgCaptionLength < 140) {
      captionPts = 26;
      findings.push(`Concise captions (avg. ${avgCaptionLength} chars)—effective for quick hits, but misses long-dwell SEO indexing.`);
      recommendations.push('Expand key educational captions to 150–400 characters using a "Hook Line → Value Bullet Points → Question CTA" framework.');
    } else if (avgCaptionLength > 750) {
      captionPts = 32;
      findings.push(`Long-form editorial captions (avg. ${avgCaptionLength} chars) maximize read-time signals.`);
    } else {
      captionPts = 12;
      findings.push(`Ultra-short captions (avg. ${avgCaptionLength} chars) provide almost no semantic keywords for Instagram Search.`);
      recommendations.push('Avoid one-line or emoji-only captions. Write a compelling first-line hook (<125 characters before truncation) followed by keyword-rich context.');
    }

    breakdown.push({
      criterion: 'Caption Depth, First-Line Hook & Dwell-Time Structure',
      pointsEarned: captionPts,
      maxPoints: 40,
      status: captionPts >= 32 ? 'optimal' : captionPts >= 22 ? 'moderate' : 'deficit',
      rationale: `Average caption length of ${avgCaptionLength} characters.`,
    });

    // 2. Conversational Triggers & Question Prompts (30 pts)
    const hasQuestionsOrPrompts = posts.some(p => /[?]|\b(comment|share|save|tag|thoughts|below|which)\b/i.test(p.caption || ''));
    let promptPts = hasQuestionsOrPrompts ? 30 : 14;
    if (hasQuestionsOrPrompts) {
      findings.push('Captions actively include conversational prompts or save/share triggers that stimulate comment velocity.');
    } else {
      findings.push('Captions broadcast statements without asking questions or prompting audience interaction.');
      recommendations.push('Close every caption with a specific low-friction question or "Save this for your next [use case]" prompt to boost algorithmic weight.');
    }

    breakdown.push({
      criterion: 'Conversational CTAs & Save/Comment Triggers',
      pointsEarned: promptPts,
      maxPoints: 30,
      status: promptPts >= 25 ? 'optimal' : 'moderate',
      rationale: hasQuestionsOrPrompts ? 'Interactive triggers present in captions.' : 'Missing question or save prompts.',
    });

    // 3. Hashtag & Semantic Keyword Hygiene (30 pts)
    let hashtagPts = 10;
    if (avgHashtags >= 3 && avgHashtags <= 10) {
      hashtagPts = 30;
      findings.push(`Disciplined hashtag strategy (avg. ${avgHashtags} tags/post) aligns with Instagram’s 3–8 niche-tag recommendation.`);
    } else if (avgHashtags > 10 && avgHashtags <= 20) {
      hashtagPts = 20;
      findings.push(`Moderate-to-high hashtag volume (avg. ${avgHashtags} tags/post).`);
      recommendations.push('Consolidate hashtags to 4–8 hyper-relevant niche and category tags rather than 15+ broad tags.');
    } else if (avgHashtags > 20) {
      hashtagPts = 10;
      findings.push(`Excessive hashtag stuffing (${avgHashtags} tags/post) looks dated and dilutes topical classification.`);
      recommendations.push('Reduce hashtag blocks to 5–8 specific tags and weave natural SEO keywords directly into caption sentences.');
    } else {
      hashtagPts = 14;
      findings.push(`Minimal hashtag indexing (${avgHashtags} tags/post).`);
      recommendations.push('Include 4–7 targeted hashtags per post (2 branded/product tags, 3 niche community tags, 2 industry tags).');
    }

    breakdown.push({
      criterion: 'Hashtag Precision & Topical Indexing (3–10 Niche Tags)',
      pointsEarned: hashtagPts,
      maxPoints: 30,
      status: hashtagPts >= 25 ? 'optimal' : hashtagPts >= 18 ? 'moderate' : 'deficit',
      rationale: `Averaging ${avgHashtags} hashtags per post.`,
    });

    const totalScore = Math.min(100, captionPts + promptPts + hashtagPts);

    return {
      categoryId: 'copywriting_discoverability',
      categoryName: 'Captions, Hooks & Discoverability',
      shortLabel: 'Copy & SEO',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'First-line caption hooks, average caption depth, semantic keyword density, conversational comment triggers, and hashtag hygiene.',
      whyItMatters: 'Instagram indexes full caption text for search queries and measures how many users tap "more" and spend >5 seconds reading.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: 'Optimal brand captions range from 180–450 characters, end with a conversational hook, and use 4–8 niche hashtags.',
      weight: 1.1,
    };
  }

  // ---------------------------------------------------------------------------
  // PILLAR 5: Engagement Quality & Audience Trust
  // ---------------------------------------------------------------------------
  private evaluateEngagementAndAudienceTrust(
    data: InstagramPublicData,
    insights?: InstagramInsightsData
  ): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    const erPct = this.calculateEngagementRatePct(data);
    const tierBenchmark = this.getFollowerTierBenchmark(data.followersCount);

    // 1. Tier-Adjusted Engagement Rate (45 pts)
    let erPts = 15;
    if (erPct >= tierBenchmark.excellent) {
      erPts = 45;
      findings.push(`Outperforming engagement rate (${erPct.toFixed(2)}%)—exceeds the ${tierBenchmark.excellent}% top-quartile benchmark for ${tierBenchmark.label} accounts.`);
    } else if (erPct >= tierBenchmark.good) {
      erPts = 34;
      findings.push(`Healthy engagement rate (${erPct.toFixed(2)}%)—above the ${tierBenchmark.good}% industry median for ${tierBenchmark.label} accounts.`);
    } else if (erPct >= tierBenchmark.fair) {
      erPts = 22;
      findings.push(`Moderate engagement rate (${erPct.toFixed(2)}%) vs. ${tierBenchmark.good}% benchmark for ${tierBenchmark.label} accounts.`);
      recommendations.push('Boost engagement rate by pruning ghost followers, replying to comments within 60 minutes of posting, and testing stronger 2-second Reel hooks.');
    } else {
      erPts = 10;
      findings.push(`Below-benchmark engagement rate (${erPct.toFixed(2)}%) indicates passive audience or low feed reach.`);
      recommendations.push('Shift from promotional announcements to high-utility Carousels and POV Reels to reactivate dormant followers.');
    }

    breakdown.push({
      criterion: `Tier-Adjusted Engagement Rate (${tierBenchmark.label} Benchmark)`,
      pointsEarned: erPts,
      maxPoints: 45,
      status: erPts >= 34 ? 'optimal' : erPts >= 22 ? 'moderate' : 'deficit',
      rationale: `${erPct.toFixed(2)}% engagement rate vs. ${tierBenchmark.good}% target.`,
    });

    // 2. Follower-to-Following Authority Ratio (25 pts)
    const ratio = data.followingCount > 0 ? data.followersCount / data.followingCount : data.followersCount;
    let ratioPts = 10;
    if (ratio >= 5 && data.followingCount < 1500) {
      ratioPts = 25;
      findings.push(`Strong authority ratio (${ratio.toFixed(1)}:1 follower-to-following ratio) reinforces organic brand desirability.`);
    } else if (ratio >= 1.5) {
      ratioPts = 18;
      findings.push(`Positive follower-to-following ratio (${ratio.toFixed(1)}:1).`);
      if (data.followingCount > 1200) {
        recommendations.push('Curate your Following list below 800 relevant industry partners and creators to elevate brand authority perception.');
      }
    } else {
      ratioPts = 8;
      findings.push(`Low follower-to-following ratio (${ratio.toFixed(2)}:1) can signal follow-for-follow tactics to discerning consumers.`);
      recommendations.push('Unfollow inactive or unrelated accounts so your follower count substantially exceeds your following count.');
    }

    breakdown.push({
      criterion: 'Follower-to-Following Authority Ratio',
      pointsEarned: ratioPts,
      maxPoints: 25,
      status: ratioPts >= 20 ? 'optimal' : ratioPts >= 15 ? 'moderate' : 'deficit',
      rationale: `${ratio.toFixed(1)}:1 ratio (${data.followersCount.toLocaleString()} followers / ${data.followingCount.toLocaleString()} following).`,
    });

    // 3. Conversation Depth (Comment-to-Like Ratio & Insights Saves/Shares) (30 pts)
    const totalLikes = data.recentPosts.reduce((s, p) => s + p.likesCount, 0);
    const totalComments = data.recentPosts.reduce((s, p) => s + p.commentsCount, 0);
    const commentToLikePct = totalLikes > 0 ? (totalComments / totalLikes) * 100 : 0;

    let depthPts = 12;
    if (commentToLikePct >= 4) {
      depthPts = 24;
      findings.push(`High conversation depth (${commentToLikePct.toFixed(1)} comments per 100 likes) signals genuine community affinity.`);
    } else if (commentToLikePct >= 1.8) {
      depthPts = 18;
      findings.push(`Healthy comment-to-like ratio (${commentToLikePct.toFixed(1)}%).`);
    } else {
      depthPts = 10;
      findings.push(`Low comment-to-like ratio (${commentToLikePct.toFixed(1)}%)—interactions are predominantly passive double-taps.`);
      recommendations.push('Pin 2 insightful community comments on every post and reply with open-ended follow-up questions to double comment volume.');
    }

    if (insights && ((insights.savesCount && insights.savesCount > 0) || (insights.sharesCount && insights.sharesCount > 0))) {
      depthPts = Math.min(30, depthPts + 6);
      findings.push(
        `Verified high-intent retention signals: ${insights.savesCount?.toLocaleString() || 0} Saves and ${insights.sharesCount?.toLocaleString() || 0} Shares recorded.`
      );
    }

    breakdown.push({
      criterion: 'Conversation Depth (Comment-to-Like Ratio & Saves/Shares)',
      pointsEarned: depthPts,
      maxPoints: 30,
      status: depthPts >= 22 ? 'optimal' : depthPts >= 16 ? 'moderate' : 'deficit',
      rationale: `${commentToLikePct.toFixed(1)}% comment-to-like ratio.`,
    });

    const totalScore = Math.min(100, erPts + ratioPts + depthPts);

    return {
      categoryId: 'engagement_audience_trust',
      categoryName: 'Engagement Quality & Audience Trust',
      shortLabel: 'Engagement & Trust',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'Follower-tier adjusted engagement rate, follower-to-following authority ratio, comment-to-like depth, and high-intent Saves/Shares.',
      whyItMatters: 'Vanity follower counts do not drive revenue. High comment ratios, Saves, and Shares are the primary algorithmic signals for organic reach.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: `For ${tierBenchmark.label} profiles, median engagement is ${tierBenchmark.good}% with a >2.5% comment-to-like ratio.`,
      weight: 1.5,
    };
  }

  // ---------------------------------------------------------------------------
  // PILLAR 6: Growth Velocity & Funnel Conversion Readiness
  // ---------------------------------------------------------------------------
  private evaluateGrowthAndFunnelReadiness(
    data: InstagramPublicData,
    insights?: InstagramInsightsData
  ): AuditScoreCategory {
    const breakdown: ScoreBreakdownItem[] = [];
    const findings: string[] = [];
    const recommendations: string[] = [];

    // 1. Viral Discovery & Non-Follower Reach Architecture (35 pts)
    const hasVideo = data.recentPosts.some(p => p.type === 'video');
    const postsPerWeek = data.postsPerWeek ?? 3;
    let discoveryPts = 15;

    if (hasVideo && postsPerWeek >= 3) {
      discoveryPts = 32;
      if (insights?.nonFollowerReachPct && insights.nonFollowerReachPct >= 35) {
        discoveryPts = 35;
        findings.push(`Strong top-of-funnel expansion: ${insights.nonFollowerReachPct}% of reach comes from non-followers.`);
      } else {
        findings.push('Active Reels cadence positions the profile for continuous non-follower discovery.');
      }
    } else {
      discoveryPts = 15;
      findings.push('Limited short-form video output constrains top-of-funnel reach to existing followers only.');
      recommendations.push('Publish 2–3 problem-solving or behind-the-scenes Reels weekly to reach non-followers via the Reels tab.');
    }

    breakdown.push({
      criterion: 'Top-of-Funnel Non-Follower Discovery Engine',
      pointsEarned: discoveryPts,
      maxPoints: 35,
      status: discoveryPts >= 28 ? 'optimal' : discoveryPts >= 18 ? 'moderate' : 'deficit',
      rationale: hasVideo ? 'Short-form video discovery active.' : 'Missing short-form video discovery layer.',
    });

    // 2. Profile-to-Website Conversion Readiness (35 pts)
    let conversionPts = 10;
    const hasUrl = Boolean(data.externalUrl?.trim());
    const hasCtaInBio = CTA_VERBS.some(v => new RegExp(`\\b${v}\\b`, 'i').test(data.bio || ''));

    if (hasUrl && hasCtaInBio) {
      conversionPts = 30;
      if (insights?.profileViews && insights?.websiteClicks && insights.profileViews > 0) {
        const ctr = (insights.websiteClicks / insights.profileViews) * 100;
        if (ctr >= 8) {
          conversionPts = 35;
          findings.push(`High profile-to-website conversion rate (${ctr.toFixed(1)}% tap-through from ${insights.profileViews.toLocaleString()} profile views).`);
        } else {
          findings.push(`Profile link tap-through rate is ${ctr.toFixed(1)}% (${insights.websiteClicks} clicks / ${insights.profileViews} profile visits).`);
          recommendations.push('Align your pinned top 3 grid posts with the exact offer linked in your bio to lift profile-to-click CTR above 8%.');
        }
      } else {
        findings.push('Complete bio-to-link conversion path established (Value Prop → CTA → Destination URL).');
      }
    } else if (hasUrl) {
      conversionPts = 20;
      findings.push('Destination URL is present, but bio copy lacks a strong conversion bridge.');
      recommendations.push('Add a specific lead magnet or offer callout above your bio URL to increase link clicks.');
    } else {
      conversionPts = 6;
      findings.push('Profile cannot monetize attention without an active external conversion link.');
      recommendations.push('Configure a mobile-optimized landing page URL in your bio immediately.');
    }

    breakdown.push({
      criterion: 'Profile-to-Website Funnel Conversion Architecture',
      pointsEarned: conversionPts,
      maxPoints: 35,
      status: conversionPts >= 28 ? 'optimal' : conversionPts >= 18 ? 'moderate' : 'deficit',
      rationale: hasUrl && hasCtaInBio ? 'Full CTA + URL funnel active.' : 'Incomplete conversion bridge.',
    });

    // 3. Strategic Goal & Audience Alignment (30 pts)
    let alignmentPts = 22;
    if (data.targetAudience && data.primaryBrandGoal) {
      alignmentPts = 28;
      findings.push(`Audit calibrated for target audience ("${data.targetAudience}") and primary objective (${data.primaryBrandGoal.replace('_', ' ')}).`);
    } else {
      findings.push('Baseline commercial readiness verified across profile touchpoints.');
      recommendations.push('Pin 3 strategic posts at the top of your grid: (1) Brand Origin/Value Prop, (2) Customer Proof/Results, and (3) Hero Offer.');
    }

    breakdown.push({
      criterion: 'Pinned Grid Architecture & Commercial Goal Alignment',
      pointsEarned: alignmentPts,
      maxPoints: 30,
      status: alignmentPts >= 24 ? 'optimal' : 'moderate',
      rationale: 'Evaluates commercial funnel alignment and top-of-grid anchoring.',
    });

    const totalScore = Math.min(100, discoveryPts + conversionPts + alignmentPts);

    return {
      categoryId: 'growth_funnel_readiness',
      categoryName: 'Growth Potential & Funnel Readiness',
      shortLabel: 'Growth & Funnel',
      score: totalScore,
      maxScore: 100,
      grade: this.computeGrade(totalScore),
      riskLevel: this.computeRiskLevel(totalScore),
      whatWasEvaluated: 'Non-follower discovery mechanics, profile-visit-to-website-click conversion path, pinned post strategy, and commercial readiness.',
      whyItMatters: 'Attention without conversion architecture leaks revenue. A growth-ready profile systematically turns Reels viewers into profile visitors and website buyers.',
      scoreBreakdown: breakdown,
      findings,
      recommendations,
      benchmark: 'Growth-stage brands achieve >35% non-follower reach and convert >8% of profile visits into website clicks.',
      weight: 1.2,
    };
  }

  // ---------------------------------------------------------------------------
  // COMPETITOR BENCHMARKING ENGINE
  // ---------------------------------------------------------------------------
  private evaluateCompetitor(profile: InstagramPublicData, competitor: InstagramPublicData) {
    const cleanCompHandle = this.normalizeHandle(competitor.username);
    const strengths: string[] = [];
    const weaknesses: string[] = [];
    const gaps: string[] = [];

    const pEr = this.calculateEngagementRatePct(profile);
    const cEr = this.calculateEngagementRatePct(competitor);

    const pRatio = profile.followingCount > 0 ? profile.followersCount / profile.followingCount : profile.followersCount;
    const cRatio = competitor.followingCount > 0 ? competitor.followersCount / competitor.followingCount : competitor.followersCount;

    const pCadence = profile.postsPerWeek ?? Math.max(1, profile.recentPosts.length);
    const cCadence = competitor.postsPerWeek ?? Math.max(1, competitor.recentPosts.length);

    const metrics: CompetitorMetricComparison[] = [
      {
        metric: 'Audience Scale (Followers)',
        brandValue: profile.followersCount.toLocaleString(),
        competitorValue: competitor.followersCount.toLocaleString(),
        delta: `${profile.followersCount >= competitor.followersCount ? '+' : ''}${(profile.followersCount - competitor.followersCount).toLocaleString()}`,
        winner: profile.followersCount > competitor.followersCount ? 'brand' : profile.followersCount < competitor.followersCount ? 'competitor' : 'tie',
      },
      {
        metric: 'Engagement Rate (%)',
        brandValue: `${pEr.toFixed(2)}%`,
        competitorValue: `${cEr.toFixed(2)}%`,
        delta: `${pEr >= cEr ? '+' : ''}${(pEr - cEr).toFixed(2)}%`,
        winner: pEr > cEr ? 'brand' : pEr < cEr ? 'competitor' : 'tie',
      },
      {
        metric: 'Authority Ratio (Followers:Following)',
        brandValue: `${pRatio.toFixed(1)}:1`,
        competitorValue: `${cRatio.toFixed(1)}:1`,
        delta: `${pRatio >= cRatio ? '+' : ''}${(pRatio - cRatio).toFixed(1)}x`,
        winner: pRatio > cRatio ? 'brand' : pRatio < cRatio ? 'competitor' : 'tie',
      },
      {
        metric: 'Content Archive (Total Posts)',
        brandValue: profile.postsCount.toLocaleString(),
        competitorValue: competitor.postsCount.toLocaleString(),
        delta: `${profile.postsCount >= competitor.postsCount ? '+' : ''}${(profile.postsCount - competitor.postsCount).toLocaleString()}`,
        winner: profile.postsCount > competitor.postsCount ? 'brand' : profile.postsCount < competitor.postsCount ? 'competitor' : 'tie',
      },
      {
        metric: 'Publishing Velocity (Posts/Week)',
        brandValue: `${pCadence}/wk`,
        competitorValue: `${cCadence}/wk`,
        delta: `${pCadence >= cCadence ? '+' : ''}${pCadence - cCadence}/wk`,
        winner: pCadence > cCadence ? 'brand' : pCadence < cCadence ? 'competitor' : 'tie',
      },
    ];

    if (profile.followersCount >= competitor.followersCount) {
      strengths.push(`Larger established audience footprint (${profile.followersCount.toLocaleString()} vs. ${competitor.followersCount.toLocaleString()} followers).`);
    } else {
      weaknesses.push(`Competitor @${cleanCompHandle} commands a larger follower base (${competitor.followersCount.toLocaleString()} vs. ${profile.followersCount.toLocaleString()}).`);
      gaps.push('Top-of-Funnel Audience Scale via Reels & Creator Collaborations');
    }

    if (pEr >= cEr) {
      strengths.push(`Superior audience engagement efficiency (${pEr.toFixed(2)}% vs. competitor's ${cEr.toFixed(2)}%).`);
    } else {
      weaknesses.push(`Competitor achieves higher per-post engagement (${cEr.toFixed(2)}% vs. your ${pEr.toFixed(2)}%).`);
      gaps.push('Interactive Carousel Storytelling & Comment Velocity');
    }

    if (pCadence >= cCadence) {
      strengths.push(`Higher or equal weekly publishing consistency (${pCadence}/wk vs. ${cCadence}/wk).`);
    } else {
      weaknesses.push(`Competitor publishes more frequently (${cCadence}/wk vs. ${pCadence}/wk), capturing more feed impressions.`);
      gaps.push('Weekly Publishing Cadence & Content Batching');
    }

    if (profile.username.length < competitor.username.length && !/[._]/.test(profile.username)) {
      strengths.push(`Cleaner, more memorable @handle structure than @${cleanCompHandle}.`);
    }

    const brandWins = metrics.filter(m => m.winner === 'brand').length;
    const compWins = metrics.filter(m => m.winner === 'competitor').length;

    return {
      competitorHandle: cleanCompHandle,
      overallAdvantage: (brandWins > compWins ? 'brand' : compWins > brandWins ? 'competitor' : 'balanced') as 'brand' | 'competitor' | 'balanced',
      metrics,
      strengths,
      weaknesses,
      gaps,
    };
  }

  // ---------------------------------------------------------------------------
  // HELPERS & SYNTHESIS
  // ---------------------------------------------------------------------------
  private calculateEngagementRatePct(data: InstagramPublicData): number {
    if (!data.recentPosts || data.recentPosts.length === 0 || !data.followersCount) return 0;
    const totalInteractions = data.recentPosts.reduce(
      (sum, p) => sum + (Number(p.likesCount) || 0) + (Number(p.commentsCount) || 0),
      0
    );
    const avgPerPost = totalInteractions / data.recentPosts.length;
    return (avgPerPost / Math.max(1, data.followersCount)) * 100;
  }

  private getFollowerTierBenchmark(followers: number) {
    if (followers < 10000) {
      return { label: 'Nano/Emerging (<10K)', excellent: 4.2, good: 2.5, fair: 1.4 };
    }
    if (followers < 50000) {
      return { label: 'Growth Brand (10K–50K)', excellent: 3.2, good: 1.9, fair: 1.1 };
    }
    if (followers < 250000) {
      return { label: 'Mid-Market Brand (50K–250K)', excellent: 2.5, good: 1.5, fair: 0.85 };
    }
    return { label: 'Enterprise/Macro (250K+)', excellent: 1.9, good: 1.1, fair: 0.6 };
  }

  private getEngagementTierLabel(erPct: number, followers: number): string {
    const b = this.getFollowerTierBenchmark(followers);
    if (erPct >= b.excellent) return 'Top 10% (Elite Engagement)';
    if (erPct >= b.good) return 'Above Industry Median';
    if (erPct >= b.fair) return 'Moderate / Average';
    return 'Below Benchmark (Needs Activation)';
  }

  private determineBrandArchetype(score: number, categories: AuditScoreCategory[]): string {
    if (score >= 85) return 'Category Authority Leader';
    if (score >= 74) return 'High-Growth Challenger Brand';
    const bioCat = categories.find(c => c.categoryId === 'bio_conversion_architecture');
    const engCat = categories.find(c => c.categoryId === 'engagement_audience_trust');
    if (engCat && engCat.score >= 75 && bioCat && bioCat.score < 65) {
      return 'Engaged Community with Leaky Funnel';
    }
    if (score >= 60) return 'Emerging Brand with Optimization Upside';
    return 'Under-Optimized Digital Storefront';
  }

  private evaluateDataCompleteness(
    profile: InstagramPublicData,
    insights?: InstagramInsightsData,
    competitor?: InstagramPublicData
  ) {
    const includedLayers: string[] = ['Handle & Linguistic Hygiene', 'Bio & Funnel Architecture', 'Public Engagement & Cadence Heuristics'];
    const missingLayers: string[] = [];

    let percentage = 70;
    let mode: 'Quick Handle & Bio Mode' | 'Public Profile Audit Mode' | 'Full Consultant Insights Mode' = 'Public Profile Audit Mode';

    const hasInsights = Boolean(
      insights &&
        (insights.reachCount || insights.profileViews || insights.websiteClicks || insights.savesCount || insights.sharesCount)
    );

    if (hasInsights) {
      includedLayers.push('First-Party Instagram Insights (Saves, Shares, Profile CTR)');
      percentage += 20;
      mode = 'Full Consultant Insights Mode';
    } else {
      missingLayers.push('Optional First-Party Insights (Saves, Shares, Website Clicks)');
    }

    if (competitor && competitor.username.trim()) {
      includedLayers.push(`Head-to-Head Competitor Benchmark (@${this.normalizeHandle(competitor.username)})`);
      percentage += 10;
    } else {
      missingLayers.push('Optional Competitor Benchmark Profile');
    }

    return {
      mode,
      percentage: Math.min(100, percentage),
      includedLayers,
      missingLayers,
    };
  }

  private buildPriorityActionMatrix(categories: AuditScoreCategory[]): PriorityActionItem[] {
    const sorted = [...categories].sort((a, b) => a.score - b.score);
    const actions: PriorityActionItem[] = [];

    sorted.forEach((cat, idx) => {
      if (cat.recommendations.length > 0) {
        const priority: 'Critical' | 'High' | 'Medium' =
          cat.score < 55 || idx === 0 ? 'Critical' : cat.score < 75 || idx === 1 ? 'High' : 'Medium';
        actions.push({
          priority,
          category: cat.categoryName,
          action: cat.recommendations[0],
          expectedImpact:
            cat.categoryId === 'bio_conversion_architecture'
              ? '+15% to +30% lift in profile-to-website tap-through rate'
              : cat.categoryId === 'brand_identity_profile'
              ? 'Higher ranking in Instagram Graph Search for category keywords'
              : cat.categoryId === 'content_visual_strategy'
              ? '+25% increase in non-follower Reels & Explore impressions'
              : '+18% improvement in post dwell time, Saves, and comment velocity',
        });
      }
    });

    return actions.slice(0, 5);
  }

  private generateExecutiveSummary(
    profile: InstagramPublicData,
    score: number,
    grade: string,
    categories: AuditScoreCategory[]
  ): string {
    const strongest = [...categories].sort((a, b) => b.score - a.score)[0];
    const weakest = [...categories].sort((a, b) => a.score - b.score)[0];

    if (score >= 82) {
      return `@${profile.username} achieves an overall Instagram Brand Readiness Score of ${score}/100 (Grade ${grade}), placing it in the top tier of ${profile.industry || 'consumer'} profiles. The profile excels in ${strongest.categoryName} (${strongest.score}/100). To capture incremental compounding returns, focus next on ${weakest.categoryName} (${weakest.score}/100).`;
    }
    if (score >= 65) {
      return `@${profile.username} records a solid Instagram Brand Readiness Score of ${score}/100 (Grade ${grade}) with clear structural upside. Core strengths are anchored in ${strongest.categoryName} (${strongest.score}/100), while immediate conversion and reach gains are locked behind ${weakest.categoryName} (${weakest.score}/100).`;
    }
    return `@${profile.username} currently scores ${score}/100 (Grade ${grade}), indicating high strategic friction across its Instagram storefront. While ${strongest.categoryName} (${strongest.score}/100) provides an initial foundation, urgent remediation is required in ${weakest.categoryName} (${weakest.score}/100) to prevent lost organic discovery and website conversions.`;
  }
}
